/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-dashboard.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import FormControl from '@mui/material/FormControl';
import CopyButton from '@components/core/copy-button';
import TextField from '@components/core/text-field';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useBoolean, useTranslation, useAuthorization, useSysAdminCanEditStudy } from 'hooks';
import { GET_DASHBOARDS, SAVE_DASHBOARD, GET_PANELS, GET_DATA_REVIEW_TABLES } from 'queries/central-monitoring';
import { PanelFields, DataReviewTableFields } from 'queries/generated/fragments.generated';
import { DashboardType, PanelType } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import StudyDashboardPanel from 'views/components/admin/study-dashboard-panel';
import Button from 'views/components/core/buttons/button';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import './admin-study-dashboard.scss';
import Constants from '@utils/constants';
import { checkConfigIdErrors } from '@utils/validation';
import {
    dashboards,
    dashboards_OpraQueries_dashboards_Dashboard_panels_DashboardParentPanel as DashboardsPanel,
    dashboards_OpraQueries_dashboards_Dashboard_panels_DashboardParentPanel_childPanels_DashboardChildPanel as DashboardChildPanels,
} from '@queries/generated/central-monitoring.generated';

type DashboardChildPanel = DashboardsPanel & {
    name: string;
    childPanels: DashboardChildPanels;
};

const AdminDashboard = ({ study, type }: any) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = !!user?.canWriteSystemAdminLevel?.(false, false, false, true);
    const isSysAdmin = !!user?.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const sysAdminCanEditStudy = useSysAdminCanEditStudy();
    const [dashboards, setDashboards] = useState<DashboardsPanel[]>([]);
    const [childPanel, setChildPanel] = useState<DashboardChildPanel | null>(null);
    const [configId, setConfigId] = useState(null);
    const [configIdError, setConfigIdError] = useState('');
    const secondPanel = useRef(null);

    const [loadData, { data, loading, error }] = useLazyQuery<dashboards>(GET_DASHBOARDS);
    const [loadPanels, { data: dataPanels }] = useLazyQuery(GET_PANELS);
    const [loadTables, { data: dataTables }] = useLazyQuery(GET_DATA_REVIEW_TABLES);
    const [saveDashboard] = useMutation(SAVE_DASHBOARD);

    const saving = useBoolean(false);
    const editing = useBoolean(false);
    const canBeSaved = useBoolean(true);

    useEffect(() => {
        if (study) {
            loadData({ variables: { studyId: study.id } });
            loadPanels({
                variables: {
                    studyId: study.id,
                    organisationId: study.organisation.id,
                },
            });
            loadTables({ variables: { studyId: study.id } });
        }
    }, [study]);

    const scrollToRef = (ref: any) => {
        window.scrollTo({
            top: ref.current?.offsetTop,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        if (childPanel && window.innerWidth < 1024)
            // desktop and tablet
            scrollToRef(secondPanel);
    }, [childPanel]);

    function hideNoneLegacyPanels(panels: any) {
        if (panels === undefined || panels === null) {
            return panels;
        }

        // Legacy doesn't support all of the panels we have now so make sure we hide the new none legacy
        // TRI OP Date panels, Timeline and the two Control Hub only panels
        return dataPanels?.panels.filter(
            (p) =>
                p.panelType !== PanelType.PROPORTIONS_OVER_TIME &&
                p.panelType !== PanelType.EVENT_RATES_OVER_TIME &&
                p.panelType !== PanelType.TRI_OP_DATE &&
                p.panelType !== PanelType.TIMELINE,
        );
    }

    useEffect(() => {
        if (data?.dashboards && dataPanels?.panels && dataTables?.dataReviewTablesForStudy) {
            const dashboard = data.dashboards.find((d: any) => d.type === type);
            const array =
                dashboard?.panels?.map((item) => ({
                    ...item,
                    name: dataPanels.panels.find((el: PanelFields) => el.id === item?.panelId)?.name,
                    id: item?.panelId,
                    active:
                        dataPanels.panels.find((element: PanelFields) => element.id === item?.panelId)?.active ?? false,
                    childPanels: item?.childPanels?.map((el) => ({
                        ...el,
                        name: dataPanels.panels.find((element: PanelFields) => element.id === el?.panelId)?.name ?? '',
                        active:
                            dataPanels.panels.find((element: PanelFields) => element.id === el?.panelId)?.active ??
                            false,
                        id: el?.panelId,
                    })),
                    dataReviews: item?.dataReviews?.map((el) => ({
                        ...el,
                        name:
                            dataTables.dataReviewTablesForStudy.find(
                                (element: DataReviewTableFields) => element.id === el?.dataReviewId,
                            )?.name ?? '',
                        active:
                            dataTables.dataReviewTablesForStudy.find(
                                (element: DataReviewTableFields) => element.id === el?.dataReviewId,
                            )?.active ?? false,
                        id: el?.dataReviewId,
                    })),
                })) ?? [];
            setConfigId(dashboard?.configId);
            setDashboards(array);
        }
    }, [data, dataPanels, dataTables]);

    const saveDashboardFunc = () => {
        if (!editing.value) {
            editing.setTrue();
            NavigationService.legacyShowWarningLostDataModal(true);
        } else {
            const panels = dashboards.map((item: any, index: number) => ({
                index,
                panelId: item.id,
                childPanels:
                    item?.childPanels?.map((el: any) => ({
                        index: el.index,
                        panelId: el.id,
                    })) ?? [],
                dataReviews:
                    item?.dataReviews?.map((el: any) => ({
                        index: el.index,
                        dataReviewId: el.id,
                    })) ?? [],
            }));
            saving.setTrue();
            saveDashboard({
                variables: {
                    dashboard: {
                        studyId: study.id,
                        configId,
                        type,
                        panels,
                    },
                    reason: '',
                },
            })
                .then(() => {
                    editing.setFalse();
                    saving.setFalse();
                    NavigationService.legacyShowWarningLostDataModal(false);
                    NotificationService.showNotification(t('changesSuccessfullySaved'));
                })
                .catch(() => {
                    editing.setFalse();
                    saving.setFalse();
                    NavigationService.legacyShowWarningLostDataModal(false);
                    NotificationService.showNotification(t('errorMessage'));
                });
        }
    };

    const validate = (value?: string) => {
        const dashboard = data?.dashboards?.find((d: any) => d.type === type);
        const configError = checkConfigIdErrors(dashboard, data?.dashboards, value);
        setConfigIdError(configError);
    };

    const onChange = (event: any) => {
        const { value } = event.target;

        canBeSaved.setTrue();

        setConfigId(value);
        validate(value);
    };

    if (!study) return <h3>{t('common:noStudySelected')}</h3>;

    if (!study || loading) return <Loading float />;

    if (error) return <Error error={error} />;

    const setPanelItems = (items: any, isDataReviews: boolean) => {
        const item = { ...childPanel };
        if (isDataReviews) item.dataReviews = items;
        else item.childPanels = items;
        setChildPanel(item);
        const array = [...dashboards];
        const value = array.findIndex((el: any) => el.id === item.id);
        array[value] = item;
        setDashboards(array);
    };

    return (
        <>
            <PageTitle
                text={t(type === DashboardType.STUDY ? 'admin:studyDashboardSetup' : 'admin:siteDashboardSetup')}
            />
            <br />
            <div className="summary-tab-btns-wrapper">
                {configId !== null && configId !== undefined && (
                    <FormControl variant="outlined" className="form-control-type dashboard-config-id">
                        <TextField
                            readOnly={!sysAdminCanEditStudy || !editing.value}
                            id="edit-field-config-id"
                            label={t('admin:configId')}
                            onChange={onChange}
                            fullWidth
                            value={configId}
                            InputProps={{
                                readOnly: !sysAdminCanEditStudy || !editing.value,
                                endAdornment: <CopyButton value={configId ?? ''} />,
                            }}
                            helperText={
                                <span style={{ color: '#ae0015' }} className="remaining-text">
                                    {configIdError}
                                </span>
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!configIdError}
                        />
                    </FormControl>
                )}
                {saving.value && <p className="saving-status">{t('saving')}</p>}
                <Button
                    className="summary-save-btn main-button edit-button"
                    disabled={
                        saving.value || !canBeSaved.value || !(canEdit || sysAdminCanEditStudy) || !!configIdError
                    }
                    text={!editing.value ? 'common:edit' : 'common:done'}
                    id="dashboard-save-btn"
                    onClick={saveDashboardFunc}
                />
            </div>
            <div className="panels-wrapper">
                <div className="main-dashboard">
                    <ItemWrapper
                        title={t('admin:mainDashboard')}
                        hideItemNumber
                        removePadding
                        customStyle={{ backgroundColor: '#f3f3f4' }}
                    >
                        <StudyDashboardPanel
                            panelItems={dashboards}
                            setChildPanel={setChildPanel}
                            childPanel={childPanel}
                            disabled={!editing.value || isSysAdmin}
                            title={t('admin:mainDisplayPanels')}
                            setPanelItems={setDashboards}
                            itemsForAdding={hideNoneLegacyPanels(dataPanels?.panels ?? [])}
                        />
                    </ItemWrapper>
                </div>
                {!childPanel && (
                    <div className="no-child-panel-wrapper">
                        <p className="no-child-panel-placeholder">{t('admin:selectPanelToViewChildPanels')}</p>
                    </div>
                )}
                {!!childPanel && (
                    <ItemWrapper
                        title={childPanel.name}
                        hideItemNumber
                        removePadding
                        customStyle={{ backgroundColor: '#f3f3f4' }}
                        customRef={secondPanel}
                    >
                        <div className="secondary-dashboard">
                            <div className="secondary-display-panel" style={{ borderRight: '1px solid #c3c3c8' }}>
                                <StudyDashboardPanel
                                    panelItems={childPanel.childPanels ?? []}
                                    disabled={!editing.value || isSysAdmin}
                                    childPanel={childPanel}
                                    itemsForAdding={hideNoneLegacyPanels(dataPanels?.panels ?? [])}
                                    title={t('admin:secondaryDisplayPanels')}
                                    setPanelItems={(items: any) => setPanelItems(items, false)}
                                    customKey="secondary-panels"
                                />
                            </div>
                            <div className="secondary-display-panel" style={{ borderLeft: '1px solid #c3c3c8' }}>
                                <StudyDashboardPanel
                                    panelItems={childPanel.dataReviews ?? []}
                                    disabled={!editing.value || isSysAdmin}
                                    itemsForAdding={dataTables?.dataReviewTablesForStudy ?? []}
                                    title={t('admin:dataReviewSets')}
                                    setPanelItems={(items: any) => setPanelItems(items, true)}
                                    customKey="data-review-set"
                                />
                            </div>
                        </div>
                    </ItemWrapper>
                )}
            </div>
        </>
    );
};

export default AdminDashboard;
